import {
  CalendarIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  HomeIcon,
} from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import { ICourse, IKDTKlass } from '../../../../stores/courses'
import { getCourseTotalTime } from '../../utils/sec2time'
import { IApplyPeriodList } from '.'

interface IKlassInfoSectionProps {
  course: ICourse | IKDTKlass
  isKDT: boolean
  isSimplifyKDT: boolean
  isDelayCourse?: boolean
  isPreNoticeStatus?: boolean
  applyStatus: string
  applyPeriodList?: IApplyPeriodList
}

interface IDetailKlassInfo {
  icon: React.ReactNode
  title: string
  content: string
}

interface IKDTPeriodTitle {
  [status: string]: string
}

const KlassInfoSection = ({
  course,
  isKDT,
  isSimplifyKDT,
  isDelayCourse,
  isPreNoticeStatus,
  applyStatus,
  applyPeriodList,
}: IKlassInfoSectionProps) => {
  const {
    weeks,
    startedAt,
    endedAt,
    totalResources,
    totalSeconds,
    isOffline,
    level,
    flags,
    courseType,
  } = course
  const isStartUp = useMemo(() => courseType === 'kdt_startup', [courseType])

  const getEduType = (klassType: string) => {
    if (klassType === 'kdt' || klassType === 'kdt_plus' || klassType === 'kdt_startup') {
      if (flags.includes('onoffline')) {
        return '온라인 / 오프라인'
      }

      if (flags.includes('online')) {
        return '온라인'
      }

      if (flags.includes('offline')) {
        return '오프라인'
      }
    } else {
      return isOffline ? '오프라인' : '온라인'
    }

    return '온라인'
  }

  const kdtPeriodTitle: IKDTPeriodTitle = isSimplifyKDT
    ? {
        early: '모집기간',
        assignment: '모집기간',
        assignmentReview: '심사기간',
        participation: '제출기간',
        participationReview: '심사기간',
        ing: '심사기간',
      }
    : {
        apply: '모집기간',
        assignment: '과제기간',
        participation: '참가확인',
        ing: '참가확인',
      }

  const detailInfoList = useMemo(
    () => [
      {
        icon: isKDT ? (
          <ClipboardDocumentCheckIcon className="h-5 w-5" />
        ) : (
          <ChartBarIcon className="h-5 w-5" />
        ),
        title: isKDT ? kdtPeriodTitle[applyStatus] : '난이도',
        content:
          isKDT && applyPeriodList
            ? getPeriodInfo(
                applyPeriodList[applyStatus]?.startedAt,
                applyPeriodList[applyStatus]?.endedAt,
                isPreNoticeStatus,
              )
            : `${level ? levelMap[level - 1] : ''} 이상`,
      },
      {
        icon: <ClockIcon className="h-5 w-5" />,
        title: isKDT ? '강의분량' : '수업분량',
        content:
          isKDT && totalSeconds
            ? `${getCourseTotalTime(totalSeconds)} (약 ${isStartUp ? '7' : '8'}시간/일)`
            : totalResources && totalSeconds && totalResources > 0 && totalSeconds > 0
            ? `총 ${totalResources}개 수업${
                isDelayCourse ? '' : ` ∙ ${getCourseTotalTime(totalSeconds)}`
              }`
            : '',
      },
      {
        icon: <HomeIcon className="h-5 w-5" />,
        title: '수강방식',
        content: getEduType(courseType),
      },

      {
        icon: <CalendarIcon className="h-5 w-5" />,
        title: isKDT ? '교육기간' : '수강기간',
        content: endedAt?.includes('9998')
          ? '평생 소장'
          : isKDT
          ? `${dayjs(startedAt).format(isPreNoticeStatus ? 'YY.MM.DD' : 'MM.DD')} ~ ${dayjs(
              endedAt,
            ).format(isPreNoticeStatus ? 'YY.MM.DD' : 'MM.DD')} ${
              isPreNoticeStatus
                ? '(예정)'
                : `(${weeks as number}주${totalSeconds ? `/${totalSeconds / 3600}시간` : ''})`
            }`
          : `${weeks as number}주${isDelayCourse ? ' ∙ 영상 순차 공개' : ''}`,
      },
    ],
    [course, isKDT],
  )

  const kdtInfoList = useMemo(
    () => detailInfoList.splice(0, 1).concat(detailInfoList.splice(2, 1)),
    [detailInfoList],
  )

  const renderKlassDetailInfoList = (infoList: IDetailKlassInfo[]) =>
    infoList.map(({ icon, title, content }, idx) => (
      <li
        key={`${title}_${idx}`}
        className={`flex items-center text-sm ${
          isKDT && idx === 3 && ['ing', 'end'].includes(applyStatus) ? 'hidden' : ''
        }`}>
        <span className="inline-flex items-center">
          <span>{icon}</span>
          <span className="ml-2 min-w-12.5 font-semibold">{title}</span>
        </span>
        <span className="ml-2 whitespace-nowrap">{content}</span>
      </li>
    ))

  return (
    <ul className="flex flex-col space-y-3">
      {isKDT ? renderKlassDetailInfoList(kdtInfoList) : renderKlassDetailInfoList(detailInfoList)}
    </ul>
  )
}

const levelMap = ['입문', '초급', '중급', '고급', '고급이상']

const getPeriodInfo = (startedAt: string, endedAt: string, isPreNoticeStatus?: boolean) =>
  `${dayjs(startedAt)
    .format(isPreNoticeStatus ? 'YY.MM.DD' : 'MM.DD a h시')
    .replace('am', '오전')
    .replace('pm', '오후')} ~ ${dayjs(endedAt)
    .format(isPreNoticeStatus ? 'YY.MM.DD' : 'MM.DD a h시')
    .replace('am', '오전')
    .replace('pm', '오후')} ${isPreNoticeStatus ? '(예정)' : ''}`

export default KlassInfoSection
